import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCompactMoney } from '@/lib/money';
import { financeColor } from '../lib/financeColors';
import type { RevenueSourceView } from './RevenueBreakdownCard';

const TOP_N = 3;

export function topRevenueSources(sources: RevenueSourceView[], limit = TOP_N): RevenueSourceView[] {
  return [...sources]
    .filter((source) => source.amount > 0)
    .sort((left, right) => right.amount - left.amount)
    .slice(0, limit);
}

/** Figma-style Top Gross Transaction Sources — horizontal progress bars for top 3. */
export function TopRevenueSourcesCard({
  sources,
  currency,
}: {
  sources: RevenueSourceView[];
  currency: string;
}) {
  const top = topRevenueSources(sources);

  return (
    <Card className="overflow-hidden border-border/50 bg-card/80 shadow-none">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-sm font-medium text-sky-400">
          Top Gross Transaction Sources
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-4 pb-4 pt-0">
        {top.length === 0 ? (
          <p className="text-xs text-muted-foreground">No revenue sources for this period.</p>
        ) : (
          top.map((source) => {
            const color = source.color ?? financeColor(source.category ?? source.name);
            const width = Math.max(0, Math.min(100, source.percentage));
            return (
              <div key={source.category ?? source.name} className="space-y-1.5">
                <p className="text-sm font-medium text-foreground">{source.name}</p>
                <div className="flex items-center gap-3">
                  <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted/60">
                    <div
                      className="h-full rounded-full transition-[width]"
                      style={{ width: `${width}%`, backgroundColor: color }}
                    />
                  </div>
                  <div className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
                    <span className="w-10 text-right text-muted-foreground">
                      {source.percentage.toFixed(0)}%
                    </span>
                    <span className="min-w-[3.5rem] text-right font-medium text-foreground">
                      {formatCompactMoney(source.amount, currency)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
